import { useState } from 'react';
import { LogOut, Menu, X } from 'lucide-react';
import { useAuthStore } from '../../store/authStore';
import { logout as apiLogout } from '../../services/api';

/**
 * Single source of truth for the admin dashboard navigation header.
 * All admin pages should use this component.
 */

interface AdminDashboardNavProps {
  currentPage: 'dashboard' | 'templates' | 'messages' | 'mint-queue' | 'compliance' | 'audit';
}

const NAV_ITEMS = [
  { label: 'Dashboard', path: '/admin' },
  { label: 'Compliance', path: '/admin/compliance' },
  { label: 'Audit', path: '/admin/compliance-audit' },
  { label: 'Mint Queue', path: '/admin/mint-queue' },
  { label: 'Templates', path: '/admin/templates' },
  { label: 'Messages', path: '/admin/messages' },
];

export default function AdminDashboardNav({ currentPage }: AdminDashboardNavProps) {
  const { token, logout } = useAuthStore();
  const [mobileOpen, setMobileOpen] = useState(false);

  const handleLogout = async () => {
    try {
      await apiLogout(token!);
    } catch {}
    logout();
    window.location.href = '/';
  };

  const getKey = (item: typeof NAV_ITEMS[0]): AdminDashboardNavProps['currentPage'] => {
    if (item.path === '/admin') return 'dashboard';
    const key = item.path.replace('/admin/', '');
    if (key === 'compliance-audit') return 'audit';
    return key as AdminDashboardNavProps['currentPage'];
  };

  return (
    <header className="fixed top-0 left-0 right-0 z-50 glass-card border-b border-white/5 rounded-none">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
        {/* Logo + badge */}
        <div className="flex items-center gap-3">
          <img src="/fans-audio-logo.png" alt="fans.audio logo" className="w-8 h-8 object-contain" />
          <span className="font-mono font-bold text-lg">fans<span className="text-electric-cyan">.audio</span></span>
          <span className="text-xs font-mono text-text-muted px-2 py-1 rounded bg-surface border border-white/10">ADMIN</span>
        </div>

        {/* Desktop nav links */}
        <div className="hidden md:flex items-center gap-4">
          {NAV_ITEMS.map((item, idx) => {
            const isActive = getKey(item) === currentPage;
            return (
              <span key={item.label} className="flex items-center gap-4">
                {idx > 0 && <span className="text-electric-cyan/40 text-[10px]">♪</span>}
                {isActive ? (
                  <span className="text-sm text-electric-cyan font-mono">
                    {item.label}
                  </span>
                ) : (
                  <a
                    href={item.path}
                    className="text-sm text-text-muted hover:text-electric-cyan transition-colors font-mono"
                  >
                    {item.label}
                  </a>
                )}
              </span>
            );
          })}
          <span className="text-electric-cyan/40 text-[10px]">♪</span>
          <button
            onClick={handleLogout}
            title="Sign Out"
            className="p-2 text-text-muted hover:text-red-400 transition-colors rounded-lg hover:bg-white/5"
          >
            <LogOut className="w-4 h-4" />
          </button>
        </div>

        {/* Mobile: sign-out icon + hamburger */}
        <div className="flex md:hidden items-center gap-1">
          <button
            onClick={handleLogout}
            title="Sign Out"
            className="p-2 text-text-muted hover:text-red-400 transition-colors rounded-lg hover:bg-white/5"
          >
            <LogOut className="w-4 h-4" />
          </button>
          <button
            onClick={() => setMobileOpen(!mobileOpen)}
            className="p-2 text-text-secondary hover:text-white transition-colors rounded-lg hover:bg-white/5"
          >
            {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </div>

      {/* Mobile dropdown */}
      {mobileOpen && (
        <div className="md:hidden border-t border-white/5 bg-deep-charcoal/95 backdrop-blur-lg">
          <div className="max-w-7xl mx-auto px-4 py-3 flex flex-col gap-1">
            {NAV_ITEMS.map((item) => {
              const isActive = getKey(item) === currentPage;
              return isActive ? (
                <span
                  key={item.label}
                  className="text-sm text-electric-cyan font-mono px-3 py-2.5 rounded-lg bg-white/5"
                >
                  {item.label}
                </span>
              ) : (
                <a
                  key={item.label}
                  href={item.path}
                  className="text-sm text-text-muted font-mono hover:text-electric-cyan transition-colors px-3 py-2.5 rounded-lg hover:bg-white/5"
                >
                  {item.label}
                </a>
              );
            })}
          </div>
        </div>
      )}
    </header>
  );
}
